import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  SearchIcon,
  UserPlusIcon,
  UserSearchIcon,
  UserXIcon,
  UsersIcon,
  XIcon,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Field } from '@/components/field';
import { PageHeader } from '@/components/page-header';
import { PagerControls } from '@/components/pager';
import { StatCard } from '@/components/stat-card';
import { EmptyState, ErrorNotice, TableSkeleton } from '@/components/states';
import { useOffsetPager } from '@/hooks/use-offset-pager';
import { useAdminStats, useUserList } from '@/lib/admin-queries';
import { formatNumber } from '@/lib/utils';
import { formatTashkent } from '@/lib/time';
import { strings } from '@/i18n/strings';

const USER_COLUMNS = 6;

/**
 * Modul kartochkalari.
 *
 * Sanoqlar ro'yxatdan EMAS, `GET /admin/stats` dan keladi — ro'yxat sahifalangan va u faqat
 * o'z sahifasi haqida gapira oladi. "Bugun qo'shilgan" esa umuman ro'yxatdan chiqmaydi:
 * u kunlik kesim, ro'yxat esa tartiblangan oqim.
 */
function UserStats() {
  const stats = useAdminStats();
  const users = stats.data?.users;

  return (
    <div className="motion-stagger grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <StatCard
        label={strings.users.statAll}
        value={users === undefined ? '—' : formatNumber(users.total)}
        icon={UsersIcon}
      />
      <StatCard
        label={strings.users.statToday}
        value={users === undefined ? '—' : formatNumber(users.today)}
        icon={UserPlusIcon}
      />
      <StatCard
        label={strings.users.statWeek}
        value={users === undefined ? '—' : formatNumber(users.week)}
        icon={UserPlusIcon}
      />
      <StatCard
        label={strings.users.statBlocked}
        value={users === undefined ? '—' : formatNumber(users.blocked)}
        icon={UserXIcon}
        tone={(users?.blocked ?? 0) > 0 ? 'warning' : 'muted'}
      />
    </div>
  );
}

/**
 * Foydalanuvchilar ro'yxati (§5 P4.2b).
 *
 * Ro'yxat ekran ochilishi bilan chiqadi, qidiruv esa uni TORAYTIRADI. Ilgari teskarisi edi —
 * qidiruvsiz ekran bo'sh turardi — va bu operatorning eng ko'p beradigan savoliga ("kecha kim
 * qo'shildi") javob bermasdi: unga javob berish uchun avval odamning nomini bilish kerak bo'lardi.
 *
 * Kartochka endi shu sahifada emas, `/users/{id}` da. Sabab amaliy: qo'llab-quvvatlash
 * suhbatida kartochkaning manzili ulashiladi, ish yarmida uzilganda "orqaga" ishlashi kerak,
 * va ochilgan kartochka ro'yxatni pastga surib yubormasligi kerak. Uning ochilishi serverda
 * audit yozuvi qoldiradi — ya'ni bu "shunchaki qarash" emas, va o'z manziliga arziydi.
 */
export function UsersPage() {
  const [draft, setDraft] = useState('');
  const [query, setQuery] = useState('');
  const pager = useOffsetPager();

  const results = useUserList({
    ...(query === '' ? {} : { query }),
    page: pager.page,
    limit: pager.limit,
  });

  const rows = results.data?.items ?? [];

  /** Qidiruv o'zgarishi sahifalashni boshidan boshlaydi — eski sahifa yangi ro'yxatda begona. */
  const applyQuery = (next: string) => {
    setQuery(next);
    pager.reset();
  };

  return (
    <section className="flex flex-col gap-6">
      <PageHeader title={strings.users.title} description={strings.users.subtitle} />

      <UserStats />

      <Card>
        <CardHeader>
          <CardTitle>{strings.users.listHeading}</CardTitle>
          <CardDescription>{strings.users.listHint}</CardDescription>
        </CardHeader>

        <CardContent className="flex flex-col gap-4">
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              applyQuery(draft.trim());
            }}
          >
            <Field label={strings.users.searchLabel} className="w-full max-w-lg">
              <Input
                name="query"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
              />
            </Field>
            <Button type="submit">
              <SearchIcon aria-hidden="true" />
              {strings.users.search}
            </Button>
            {/*
              Tozalash tugmasi faqat qidiruv QO'LLANGANDA turadi: bo'sh ro'yxat ustida u hech
              narsa qilmaydigan tugma bo'lardi.
            */}
            {query === '' ? null : (
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setDraft('');
                  applyQuery('');
                }}
              >
                <XIcon aria-hidden="true" />
                {strings.common.reset}
              </Button>
            )}
          </form>

          {results.isError ? <ErrorNotice error={results.error} /> : null}

          {results.isSuccess && rows.length === 0 ? (
            <EmptyState
              title={query === '' ? strings.users.listEmpty : strings.users.empty}
              icon={UserSearchIcon}
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{strings.users.columnUser}</TableHead>
                  <TableHead>{strings.users.columnTelegram}</TableHead>
                  <TableHead>{strings.users.columnPhone}</TableHead>
                  <TableHead className="text-right">{strings.users.columnBalance}</TableHead>
                  <TableHead>{strings.users.columnCreatedAt}</TableHead>
                  <TableHead>{strings.moderation.columnActions}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {results.isPending ? <TableSkeleton columns={USER_COLUMNS} /> : null}
                {rows.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">
                      {user.displayName}
                      {user.username ? ` (@${user.username})` : ''}
                      {user.isBlocked ? ` · ${strings.users.blocked}` : ''}
                    </TableCell>
                    <TableCell className="tabular font-mono">{user.telegramId}</TableCell>
                    {/*
                      Raqam `tel:` havolasi: operator sovrin haqida qo'ng'iroq qilish uchun uni
                      baribir ko'chirib oladi — bitta bosishga qisqartirilgani xato ko'chirishni
                      ham yo'q qiladi.
                    */}
                    <TableCell className="tabular whitespace-nowrap">
                      {user.phoneNumber ? (
                        <a className="hover:underline" href={`tel:${user.phoneNumber}`}>
                          {user.phoneNumber}
                        </a>
                      ) : (
                        <span className="text-muted-foreground text-xs">
                          {strings.users.phoneMissing}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="tabular text-right">
                      {formatNumber(user.currentBalance)}
                    </TableCell>
                    <TableCell className="tabular whitespace-nowrap">
                      {formatTashkent(user.createdAt)}
                    </TableCell>
                    <TableCell>
                      {/*
                        Havola, tugma emas: kartochka endi o'z manzilida turadi, ya'ni uni
                        yangi oynada ochish, ulashish va "orqaga" bilan qaytish ishlaydi.
                      */}
                      <Button size="sm" variant="outline" asChild>
                        <Link to="/users/$userId" params={{ userId: user.id }}>
                          {strings.users.open}
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <PagerControls
            pager={pager}
            total={results.data?.total}
            pageCount={results.data?.pageCount}
            rowCount={rows.length}
            busy={results.isFetching}
            label={strings.users.listHeading}
          />
        </CardContent>
      </Card>
    </section>
  );
}
